import { useState } from 'react';
import { usePortfolioData, Project } from '@/hooks/use-portfolio-data';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Github,
  ExternalLink,
  ShieldAlert,
  BookOpen,
  Star,
  CheckCircle2,
  Code
} from 'lucide-react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { ProjectDialog } from '@/components/ProjectDialog';
import { cn } from '@/lib/utils';

export function Projects() {
  const { data: portfolioData, isLoading } = usePortfolioData();
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openProjectDetails = (project: Project) => {
    setSelectedProject(project);
    setIsDialogOpen(true);
  };

  const getProjectCategories = (): string[] => {
    if (!portfolioData || !portfolioData.projects) return [];
    const categories = new Set<string>();
    portfolioData.projects.forEach(p => categories.add(p.category));
    return Array.from(categories);
  };

  const filteredProjects = () => {
    if (!portfolioData || !portfolioData.projects) return [];
    return portfolioData.projects.filter(p =>
      filter === 'All' || p.category === filter
    );
  };

  if (isLoading) {
    return (
      <section id="projects" className="py-20 bg-white dark:bg-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-40 mx-auto mb-4" />
            <Skeleton className="w-16 h-1.5 mx-auto rounded-full mb-4" />
            <Skeleton className="h-5 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-96 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!portfolioData) return null;

  return (
    <section id="projects" className="py-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 animate-fade-in">
            <span className="animate-pulse mr-2">✦</span> My Engineering Work
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center tracking-tight animate-slide-up">
            Case <span className="bg-gradient-text">Studies</span>
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-primary to-secondary rounded-full mb-6 animate-slide-up animate-delay-100"></div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-center animate-slide-up animate-delay-200">
            A deep dive into the problems I've solved and the systems I've built.
          </p>
        </div>

        {/* Project Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-16 animate-fade-in animate-delay-300">
          <Button
            variant={filter === 'All' ? 'default' : 'outline'}
            className={cn(
              "rounded-full px-6 shadow-sm transition-all duration-300",
              filter === 'All' ? "bg-gradient-to-r from-primary to-secondary hover:shadow-md" : "hover:border-primary"
            )}
            onClick={() => setFilter('All')}
          >
            All Projects
          </Button>

          {getProjectCategories().map((category, idx) => (
            <Button
              key={category}
              variant={filter === category ? 'default' : 'outline'}
              className={cn(
                "rounded-full px-6 shadow-sm transition-all duration-300",
                filter === category ? "bg-gradient-to-r from-primary to-secondary hover:shadow-md" : "hover:border-primary"
              )}
              onClick={() => setFilter(category)}
              style={{ animationDelay: `${idx * 100 + 400}ms` }}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects().map((project, idx) => (
            <Card
              key={project.id}
              role="button"
              tabIndex={0}
              className="rounded-2xl overflow-hidden card-hover border-muted/30 group cursor-pointer flex flex-col h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              onClick={() => openProjectDetails(project)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openProjectDetails(project);
                }
              }}
              style={{
                animation: 'fadeIn 0.8s ease-out forwards',
                animationDelay: `${idx * 150}ms`
              }}
            >
              {/* Project Image & Badge */}
              <div className="relative h-64 overflow-hidden shrink-0 bg-slate-900 flex items-center justify-center">
                {project.image ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex flex-col items-center justify-center p-6 border-b border-muted/10">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2.5">
                      <Code className="h-6 w-6 text-primary" />
                    </div>
                    <span className="text-[10px] font-bold font-mono text-muted-foreground uppercase tracking-widest">{project.category}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-20">
                  <Button variant="secondary" className="gap-2 font-bold rounded-xl shadow-xl">
                    <BookOpen className="h-4 w-4" />
                    View Case Study
                  </Button>
                </div>
                <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                  {project.isNDA && (
                    <Badge variant="destructive" className="flex items-center gap-1.5 px-3 py-1 font-bold shadow-lg">
                      <ShieldAlert className="h-3 w-3" />
                      NDA Protected
                    </Badge>
                  )}
                  <Badge className="bg-primary/90 backdrop-blur-md border-none px-3 py-1 font-semibold text-xs shadow-md">
                    {project.category}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6 flex-grow">
                <h3 className="text-xl font-bold mb-1 group-hover:text-primary transition-colors">
                  {project.title}
                </h3>
                {project.role && (
                  <p className="text-xs font-bold text-primary mb-3 uppercase tracking-wider">{project.role}</p>
                )}

                <p className="text-slate-700 dark:text-slate-400 mb-5 line-clamp-3 text-sm leading-relaxed font-medium tracking-tight">
                  {project.description}
                </p>

                {/* Tech stack tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.slice(0, 4).map((tech) => (
                    <Badge
                      key={tech}
                      variant="secondary"
                      className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                    >
                      {tech}
                    </Badge>
                  ))}
                  {project.technologies.length > 4 && (
                    <Badge variant="outline" className="text-[10px] px-2 py-0">
                      +{project.technologies.length - 4}
                    </Badge>
                  )}
                </div>

                {/* Engineering Highlights Preview */}
                <div className="space-y-2 mt-auto">
                  {project.highlights?.slice(0, 2).map((highlight, hIdx) => (
                    <div key={hIdx} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                      <span className="line-clamp-1">{highlight}</span>
                    </div>
                  ))}
                </div>
              </CardContent>

              <CardFooter className="px-6 pb-6 pt-0 flex gap-2">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    openProjectDetails(project);
                  }}
                  className="flex-1 gap-1.5 rounded-lg font-bold text-xs"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  Details
                </Button>

                {project.demoUrl ? (
                  <a
                    href={project.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="outline" className="w-full gap-1.5 rounded-lg font-bold text-xs border-2">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Demo
                    </Button>
                  </a>
                ) : project.repoUrl && !project.isNDA ? (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="outline" className="w-full gap-1.5 rounded-lg font-bold text-xs border-2">
                      <Github className="h-3.5 w-3.5" />
                      GitHub
                    </Button>
                  </a>
                ) : (
                  <Button disabled variant="ghost" className="flex-1 gap-1.5 rounded-lg font-bold text-xs opacity-50">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    NDA
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* GitHub CTA */}
        <div className="mt-20 text-center animate-fade-in animate-delay-700">
          <div className="inline-flex items-center gap-2 mb-4 text-secondary">
            <Star className="h-5 w-5 animate-pulse" fill="currentColor" />
            <span className="font-medium">Interested in more?</span>
          </div>
          <h3 className="text-2xl font-bold mb-6">Explore my repositories</h3>
          <a
            href={portfolioData.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button
              className="gap-2 px-6 py-3.5 h-auto rounded-xl text-base shadow-lg bg-gradient-to-r from-primary to-secondary hover:shadow-primary/20 transition-all hover:scale-[1.02]"
            >
              <Github className="h-5 w-5" /> GitHub Profile
            </Button>
          </a>
        </div>
      </div>

      {/* Project Case Study Dialog */}
      <ProjectDialog
        project={selectedProject}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </section>
  );
}

export default Projects;
